import React, { useState, useEffect, useContext } from 'react';
import { withRouter, Route, Switch, Redirect } from 'react-router-dom';
import _ from 'lodash';
import { connect } from 'react-redux';
import GenericListPair from 'modules/common/components/GenericListPair';
import ConfirmAlert from 'modules/common/components/ConfirmAlert';
import useDebounce from 'modules/common/components/UseDebounce';
import { ModalContext } from 'App';
import PropTypes from 'prop-types';
import * as actions from '../action';
import * as c from '../constant';
import CreateModal from '../components/CreateModal';
import UpdateModal from '../components/UpdateModal';
import NewTaxRevenuesContent from './NewTaxRevenuesContent';
import CreateCalculationsModal from '../components/CreateCalculationsModal';
import UpdateCalculationsModal from '../components/UpdateCalculationsModal';

const form = {
  keyword: '',
};

function BusinessOtherFeeContainer({
  match,
  history,
  getNewTaxRevenuesList,
  getNewTaxRevenues,
  isLoading,
  removeNewTaxRevenues,
  getNewTaxRevenuesPager,
  getSingleNewTaxRevenues,
  removeCalculations,
  getCalculationsList,
  ordinanceRevisionId,
}) {
  const [formData, setFormData] = useState(form);
  const setModal = useContext(ModalContext);

  const handleOnChange = e => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleAddModal = e => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: <CreateModal ordinanceRevisionId={ordinanceRevisionId} />,
      title: 'Add Tax Revenue',
      modalSize: 'modal-md',
    });
  };

  const handleEdit = data => e => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: (
        <UpdateModal
          updateList={data.attributes}
          id={data.id}
          newTaxRevenuesInfo={data}
          ordinanceRevisionId={ordinanceRevisionId}
        />
      ),
      title: 'Update Tax Revenue',
      modalSize: 'modal-md',
    });
  };

  const handleRemove = data => e => {
    e.preventDefault();
    ConfirmAlert(
      'Are you sure, you want to remove this tax?',
      () => {
        removeNewTaxRevenues(data.id, () => {
          setFormData({
            ...formData,
            keyword: '',
          });
          getNewTaxRevenuesList({ tax_revenue_type_id: 3 });
          const id = _.get(getNewTaxRevenues, '1.id');
          history.push(`/bpls/tax-revenue/sanitary/${id}`);
          getSingleNewTaxRevenues(id);
        });
      },
      () => {},
      {
        yesLabel: 'Confirm',
        noLabel: 'Cancel',
      }
    );
  };

  const debouncedSearchTerm = useDebounce(formData, 500);

  const param = {
    tax_revenue_type_id: 3,
    q: formData.keyword,
    sort_by: 'asc',
    ordinance_revision_id: ordinanceRevisionId,
  };

  useEffect(() => {
    if (debouncedSearchTerm && ordinanceRevisionId) {
      getNewTaxRevenuesList({
        tax_revenue_type_id: 3,
        ordinance_revision_id: ordinanceRevisionId,
        q: formData.keyword,
        sort_by: 'desc',
      });
    } else {
      setFormData([]);
    }
  }, [debouncedSearchTerm, ordinanceRevisionId]);

  const handleAddCalculationsModal = id => () => {
    setModal({
      isOpen: true,
      content: <CreateCalculationsModal id={id} />,
      title: 'Add Calculations',
      modalSize: 'modal-md',
    });
  };

  const handleEditCalculations = (id, data) => e => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: (
        <UpdateCalculationsModal
          updateList={data.attributes}
          id={id}
          calculationsId={data.id}
          calculationsInfo={data}
        />
      ),
      title: 'Update Calculations',
      modalSize: 'modal-md',
    });
  };

  const handleRemoveCalculations = (id, data) => e => {
    e.preventDefault();
    ConfirmAlert(
      'Are you sure you want to remove this calculations?',
      () => {
        removeCalculations(id, data.id, () => {
          getCalculationsList(id);
        });
      },
      () => {},
      {
        yesLabel: 'Confirm',
        noLabel: 'Cancel',
      }
    );
  };

  return (
    <Route
      path={`${match.path}/:newTaxRevenuesId?`}
      render={({ match: { params } }) => (
        <GenericListPair
          moduleName="Tax Revenue (Sanitary)"
          getData={getNewTaxRevenuesList}
          setParams={param}
          setKeyword={handleOnChange}
          keyword={_.get(formData, 'keyword') || ''}
          keywordPlaceholder="Search..."
          pathName={`${match.path}/:newTaxRevenuesId?`}
          listName="newTaxRevenues"
          label="Tax Revenues"
          renderItem={item => (
            <div className="p-3">{_.get(item, 'attributes.name')}</div>
          )}
          isLoading={isLoading}
          selected={params.newTaxRevenuesId}
          data={getNewTaxRevenues}
          dataPager={getNewTaxRevenuesPager}
          handleSelectRow={data => {
            history.push(`${match.url}/${_.get(data, 'id')}`);
          }}
          renderContent={() => (
            <React.Fragment>
              <Switch>
                <Route
                  path={`${match.path}/:newTaxRevenuesId?`}
                  render={() => (
                    <NewTaxRevenuesContent
                      ordinanceRevisionId={ordinanceRevisionId}
                      handleAddModal={handleAddModal}
                      handleEdit={handleEdit}
                      handleRemove={handleRemove}
                      handleAddCalculationsModal={handleAddCalculationsModal}
                      handleEditCalculations={handleEditCalculations}
                      handleRemoveCalculations={handleRemoveCalculations}
                    />
                  )}
                />
              </Switch>
              <Route
                path={`${match.path}/:newTaxRevenuesId?`}
                render={() =>
                  !params.id && getNewTaxRevenues.length > 0 ? (
                    <Redirect
                      to={`${match.url}/${_.get(getNewTaxRevenues, '0.id')}`}
                    />
                  ) : null
                }
              />
            </React.Fragment>
          )}
        />
      )}
    />
  );
}

BusinessOtherFeeContainer.propTypes = {
  match: PropTypes.instanceOf(Object).isRequired,
  history: PropTypes.instanceOf(Object).isRequired,
  getNewTaxRevenuesList: PropTypes.instanceOf(Function).isRequired,
  getNewTaxRevenues: PropTypes.instanceOf(Array).isRequired,
  getNewTaxRevenuesPager: PropTypes.instanceOf(Object).isRequired,
  removeNewTaxRevenues: PropTypes.instanceOf(Function).isRequired,
  isLoading: PropTypes.bool.isRequired,
  getSingleNewTaxRevenues: PropTypes.instanceOf(Function).isRequired,
  removeCalculations: PropTypes.instanceOf(Function).isRequired,
  getCalculationsList: PropTypes.instanceOf(Function).isRequired,
  ordinanceRevisionId: PropTypes.number.isRequired,
};

const mapStateToProps = ({ api }) => ({
  getNewTaxRevenues:
    _.get(api, `${c.GET_NEW_TAX_REVENUES_SANITARY}.list`) || [],
  getNewTaxRevenuesPager:
    _.get(api, `${c.GET_NEW_TAX_REVENUES_SANITARY}.pager`) || {},
  isLoading:
    _.get(api, 'loading', []).indexOf(c.GET_NEW_TAX_REVENUES_SANITARY) > -1,
});

const enhance = _.flowRight([withRouter, connect(mapStateToProps, actions)]);

export default enhance(BusinessOtherFeeContainer);
